/**
 * Picks the app's accent color. The admin sets one hex; the app derives all
 * eleven shades and a readable text color from it, so the preview here is the
 * whole ramp rather than a single swatch.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BRAND_STOPS,
  DEFAULT_BRAND_SEED,
  brandColorSchema,
  deriveBrandRamp,
  readableForeground,
} from "@alpha/shared";
import { Field } from "./ui";

interface BrandColorFieldProps {
  /** `#rrggbb`, or null for the app's own blue. */
  value: string | null;
  onChange: (value: string | null) => void;
}

const normalize = (raw: string): string | null => {
  const parsed = brandColorSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

export function BrandColorField({ value, onChange }: BrandColorFieldProps) {
  // Typing "#7c3aed" passes through "#7c3" — invalid, but not a mistake yet.
  // The text box keeps its own state so a half-typed color isn't rejected
  // out from under the cursor; `onChange` only fires on a complete one.
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);

  const seed = value ?? DEFAULT_BRAND_SEED;
  const { ramp, foreground } = useMemo(
    () => ({ ramp: deriveBrandRamp(seed), foreground: readableForeground(seed) }),
    [seed],
  );

  const commit = (raw: string) => {
    setText(raw);
    if (!raw.trim()) return onChange(null);
    const hex = normalize(raw);
    if (hex) onChange(hex);
  };

  return (
    <Field
      label="Brand color"
      hint="Sets the accent color used by buttons, tabs, badges, links, and the logo plate. The app builds its lighter and darker shades from this one color."
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label="Brand color picker"
            value={seed}
            onChange={(e) => commit(e.target.value)}
            className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-slate-300 bg-white p-1"
          />
          <input
            value={text}
            onChange={(e) => commit(e.target.value)}
            spellCheck={false}
            maxLength={7}
            placeholder={DEFAULT_BRAND_SEED}
            aria-label="Brand color hex"
            className="w-32 rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-800 shadow-sm transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          {value ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-sm font-medium text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
            >
              Reset to default
            </button>
          ) : (
            <span className="text-sm text-slate-400">Using the default blue</span>
          )}
        </div>

        <div>
          <div className="flex overflow-hidden rounded-lg ring-1 ring-slate-200">
            {BRAND_STOPS.map((stop) => (
              <div
                key={stop}
                title={`${stop}`}
                className="h-7 flex-1"
                style={{ backgroundColor: `rgb(${ramp[stop]})` }}
              />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span
              className="inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm"
              style={{ backgroundColor: `rgb(${ramp[600]})`, color: `rgb(${foreground})` }}
            >
              Register a team
            </span>
            <span className="text-xs text-slate-500">
              Button text flips to stay readable on light colors.
            </span>
          </div>
        </div>
      </div>
    </Field>
  );
}
